<template>
  <div class="flex flex-col gap-2 relative">
    <label class="block text-h5 font-sm font-archivo text-primary">{{ label }}</label>

    <!-- Search Input -->
    <input
        v-model="searchTerm"
        @input="filterOptions"
        @focus="showDropdown = true"
        @blur="hideDropdown"
        :placeholder="placeholder"
        class="w-full border px-4 py-2 rounded-md text-primary font-archivo text-paragraph focus:ring-primary focus:border-primary focus:outline-hidden focus:ring-0"
        :class="showDropdown && filteredOptions.length ? 'rounded-bl-none rounded-br-none' : ''"
    />

    <!-- Dropdown Options -->
    <div
        v-if="showDropdown && filteredOptions.length"
        class="absolute top-[62px] z-10 w-full bg-card border border-primary border-t-0 rounded-md mt-1 max-h-48 overflow-y-auto rounded-tl-none rounded-tr-none"
    >
      <div
          v-for="option in filteredOptions"
          :key="getOptionKey(option)"
          @mousedown="selectOption(option)"
          class="px-4 py-2 hover:bg-primary hover:text-card cursor-pointer flex items-center justify-between"
          :class="isSelected(option) ? 'bg-primary-light text-primary' : ''"
      >
        <span>{{ getOptionDisplay(option) }}</span>
        <span v-if="isSelected(option)" class="text-green-600">✓</span>
      </div>
    </div>

    <!-- Selected Pills -->
    <div class="flex flex-wrap gap-2" v-if="model && model.length > 0">
      <div
          v-for="(selectedOption, index) in model"
          :key="getOptionKey(selectedOption)"
          class="bg-white text-primary px-3 py-1 border border-tertiary-500 rounded-full flex items-center gap-2"
      >
        {{ getOptionDisplay(selectedOption) }}
        <button @click="removeOption(index)" class="text-primary hover:text-red-600">×</button>
      </div>
    </div>

    <!-- Error Message -->
    <h5 v-if="error" class="text-danger mt-2">
      {{ error }}
    </h5>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

// Generic interface for options
interface OptionItem {
  value: string
  label: string
  [key: string]: any
}

type Option = string | OptionItem

const model = defineModel<Option[]>()

const props = withDefaults(defineProps<{
  options?: Option[]
  label: string
  placeholder: string
  apiEndpoint: string
  error?: string
  maxSelections?: number
  optionKey?: string // For object options, which property to use as key
  optionDisplay?: string // For object options, which property to display
  dataType?: 'regions' | 'languages' | 'generic'
}>(), {
  maxSelections: 3,
  optionKey: 'value',
  optionDisplay: 'label',
  dataType: 'generic'
})

// Reactive state
const searchTerm = ref('')
const showDropdown = ref(false)
const availableOptions = ref<Option[]>(props.options || [])
const loading = ref(false)

// Computed properties
const filteredOptions = computed(() => {
  if (!searchTerm.value) return availableOptions.value

  return availableOptions.value.filter(option => {
    const searchText = getOptionDisplay(option).toLowerCase()
    const searchKey = getOptionKey(option).toLowerCase()
    const term = searchTerm.value.toLowerCase()

    return searchText.includes(term) || searchKey.includes(term)
  })
})

// Helper functions
const getOptionKey = (option: Option): string => {
  if (typeof option === 'string') return option
  return option[props.optionKey] || option.value || ''
}

const getOptionDisplay = (option: Option): string => {
  if (typeof option === 'string') return option
  return option[props.optionDisplay] || option.label || option.value || ''
}

const isSelected = (option: Option): boolean => {
  if (!model.value) return false
  return model.value.some(selected => getOptionKey(selected) === getOptionKey(option))
}

const selectOption = (option: Option) => {
  if (!model.value) model.value = []

  const current = model.value
  const optionKey = getOptionKey(option)

  // Check if already selected
  if (current.some(selected => getOptionKey(selected) === optionKey)) {
    // Remove if already selected (toggle behavior)
    model.value = current.filter(selected => getOptionKey(selected) !== optionKey)
  } else {
    // Add if not selected and under max limit
    if (current.length < props.maxSelections) {
      model.value = [...current, option]
    }
  }

  searchTerm.value = ''
  showDropdown.value = false
}

const removeOption = (index: number) => {
  if (model.value) {
    model.value.splice(index, 1)
  }
}

const filterOptions = () => {
  showDropdown.value = true
}

const hideDropdown = () => {
  setTimeout(() => showDropdown.value = false, 100)
}

const processRegionsData = (data: any[]): OptionItem[] => {
  const uniqueRegions = new Set<string>()
  const uniqueSubregions = new Set<string>()

  // Extract unique regions and subregions
  data.forEach(country => {
    if (country.region) {
      uniqueRegions.add(country.region)
    }
    if (country.subregion) {
      uniqueSubregions.add(country.subregion)
    }
  })

  const options: OptionItem[] = []

  // Add main regions
  uniqueRegions.forEach(region => {
    options.push({
      value: region.toLowerCase().replace(/\s+/g, '-'),
      label: region
    })
  })

  // Add subregions
  uniqueSubregions.forEach(subregion => {
    options.push({
      value: subregion.toLowerCase().replace(/\s+/g, '-'),
      label: subregion
    })
  })

  // Sort alphabetically by label
  return options.sort((a, b) => a.label.localeCompare(b.label))
}

const fetchData = async () => {
  loading.value = true
  try {
    const response = await fetch(props.apiEndpoint)
    const data = await response.json()

    if (props.dataType === 'regions') {
      availableOptions.value = processRegionsData(data)
    } else {
      availableOptions.value = data.data || data
    }
  } catch (error) {
    console.error('Failed to fetch options:', error)

    if (props.dataType === 'languages') {
      availableOptions.value = getLanguageData()
    } else if (props.dataType === 'regions') {
      availableOptions.value = getRegionData()
    }
  } finally {
    loading.value = false
  }
}

// Language and Region specific data
const getLanguageData = () => {
  return [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ar', label: 'Arabic' },
    { value: 'hi', label: 'Hindi' },
    { value: 'bn', label: 'Bengali' },
    { value: 'ur', label: 'Urdu' },
    { value: 'id', label: 'Indonesian' },
    { value: 'ms', label: 'Malay' },
    { value: 'th', label: 'Thai' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'tr', label: 'Turkish' },
    { value: 'pl', label: 'Polish' },
    { value: 'nl', label: 'Dutch' },
    { value: 'sv', label: 'Swedish' },
    { value: 'no', label: 'Norwegian' },
    { value: 'da', label: 'Danish' },
    { value: 'fi', label: 'Finnish' },
    { value: 'el', label: 'Greek' },
    { value: 'he', label: 'Hebrew' },
    { value: 'cs', label: 'Czech' },
    { value: 'sk', label: 'Slovak' },
    { value: 'hu', label: 'Hungarian' }
  ]
}

const getRegionData = () => {
  return [
    // Main Regions
    { value: 'americas', label: 'Americas' },
    { value: 'africa', label: 'Africa' },
    { value: 'asia', label: 'Asia' },
    { value: 'europe', label: 'Europe' },
    { value: 'oceania', label: 'Oceania' },
    { value: 'antarctic', label: 'Antarctic' },

    // Subregions
    { value: 'caribbean', label: 'Caribbean' },
    { value: 'central-america', label: 'Central America' },
    { value: 'north-america', label: 'North America' },
    { value: 'south-america', label: 'South America' },

    { value: 'eastern-africa', label: 'Eastern Africa' },
    { value: 'middle-africa', label: 'Middle Africa' },
    { value: 'northern-africa', label: 'Northern Africa' },
    { value: 'southern-africa', label: 'Southern Africa' },
    { value: 'western-africa', label: 'Western Africa' },

    { value: 'central-asia', label: 'Central Asia' },
    { value: 'eastern-asia', label: 'Eastern Asia' },
    { value: 'south-eastern-asia', label: 'South-Eastern Asia' },
    { value: 'southern-asia', label: 'Southern Asia' },
    { value: 'western-asia', label: 'Western Asia' },

    { value: 'central-europe', label: 'Central Europe' },
    { value: 'eastern-europe', label: 'Eastern Europe' },
    { value: 'northern-europe', label: 'Northern Europe' },
    { value: 'southeast-europe', label: 'Southeast Europe' },
    { value: 'southern-europe', label: 'Southern Europe' },
    { value: 'western-europe', label: 'Western Europe' },

    { value: 'australia-and-new-zealand', label: 'Australia and New Zealand' },
    { value: 'melanesia', label: 'Melanesia' },
    { value: 'micronesia', label: 'Micronesia' },
    { value: 'polynesia', label: 'Polynesia' }
  ]
}

// Initialize data on mount
onMounted(async () => {
  await fetchData()
})
</script>